import { ShareableState } from "@prisma/client";
import {
  Badge,
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@workspace/ui/components";
import { Search, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "~/utils/api";
import { CODEWORDS_LIBRARY_USER_ID } from "~/utils/consts";

export const DevPanelTemplateGalleryData = () => {
  const [templateServices] = api.service.getTemplateServices.useSuspenseQuery();
  const utils = api.useUtils();

  const editServiceMutation = api.service.editService.useMutation({
    onSuccess: async () => {
      await utils.service.getTemplateServices.invalidate();
      toast.success("Template updated successfully");
    },
    onError: (error) => {
      toast.error(`Failed to update template: ${error.message}`);
    },
  });

  // State for adding a new template
  const [serviceIdInput, setServiceIdInput] = useState("");

  // Get service by ID API - simplified with manual refetching
  const getServiceQuery = api.service.getService_INTERNAL.useQuery(
    { id: serviceIdInput },
    {
      enabled: false,
    }
  );

  useEffect(() => {
    if (getServiceQuery.error) {
      toast.error(`Service not found: ${getServiceQuery.error.message}`);
    }
  }, [getServiceQuery.error]);

  // Handler for search button
  const handleSearch = () => {
    if (serviceIdInput) {
      void getServiceQuery.refetch();
    }
  };

  // Function to remove a template
  const removeTemplate = (id: string) => {
    editServiceMutation.mutate({
      id,
      data: {
        isTemplate: false,
      },
    });
  };

  // Function to add a template
  const addTemplate = (id: string) => {
    editServiceMutation.mutate({
      id,
      data: {
        isTemplate: true,
        requestPermissionState: ShareableState.PUBLIC,
      },
    });
    setServiceIdInput("");
  };

  const foundService = getServiceQuery.data;
  const isCodeWordsLibraryOwned =
    foundService?.ownerId === CODEWORDS_LIBRARY_USER_ID;

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="space-y-2">
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <Input
                placeholder="Enter service ID"
                value={serviceIdInput}
                onChange={(e) => setServiceIdInput(e.target.value)}
              />
              <Button
                size="sm"
                onClick={handleSearch}
                disabled={!serviceIdInput || getServiceQuery.isFetching}
              >
                <Search size={16} />
              </Button>
            </div>

            {getServiceQuery.isFetching && <div>Searching...</div>}

            {foundService && (
              <div className="space-y-2 rounded-md border p-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium">{foundService.name}</h3>
                    <p className="text-xs text-gray-500">
                      ID: {foundService.id}
                    </p>
                    <p className="text-xs text-gray-500">
                      Owner: {foundService.owner.email}
                    </p>
                    <div className="mt-1 flex items-center gap-2">
                      <Badge
                        className="rounded-md text-xs"
                        variant={isCodeWordsLibraryOwned ? "green" : "yellow"}
                      >
                        {isCodeWordsLibraryOwned
                          ? "CodeWords Library Owned"
                          : "Non-CodeWords Library Owned"}
                      </Badge>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => addTemplate(foundService.id)}
                    disabled={
                      !!foundService.isTemplate || editServiceMutation.isPending
                    }
                  >
                    {foundService.isTemplate
                      ? "Already a Template"
                      : "Add as Template"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>ID</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Template?</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templateServices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center">
                    No templates found
                  </TableCell>
                </TableRow>
              ) : (
                templateServices.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell className="max-w-[200px] truncate text-xs">
                      {template.name}
                    </TableCell>
                    <TableCell className="max-w-[150px] truncate text-xs">
                      {template.id}
                    </TableCell>
                    <TableCell className="max-w-[150px] truncate text-xs">
                      {template.ownerId}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeTemplate(template.id)}
                      >
                        <X size={16} className="text-zinc-600" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};
